import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Container,
  Alert,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ScienceIcon from '@mui/icons-material/Science';
import BloodtypeIcon from '@mui/icons-material/Bloodtype';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useKeycloak } from '@react-keycloak/web';
import { useTranslation } from 'react-i18next';
import HeroSection from '../components/Landing/HeroSection';
import ProblemSolutionSection from '../components/Landing/ProblemSolutionSection';
import FeaturesShowcase from '../components/Landing/FeaturesShowcase';
import FinalCTA from '../components/Landing/FinalCTA';

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { keycloak, initialized } = useKeycloak();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isAuthenticated = keycloak?.authenticated || false;
  const user = keycloak?.tokenParsed;

  // Wait for Keycloak to initialize before rendering
  // This prevents flash of wrong content for authenticated users
  // SEO note: Modern search engines wait for JS execution, so they'll see the landing page after brief initialization
  if (!initialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show landing page for non-authenticated users
  if (!isAuthenticated) {
    return (
      <Box sx={{ mt: 0, pt: 0 }}>
        <HeroSection />
        <ProblemSolutionSection />
        <FeaturesShowcase />
        <FinalCTA />
      </Box>
    );
  }

  const features = [
    {
      title: t('home.features.carbCatalog.title'),
      description: t('home.features.carbCatalog.description'),
      icon: <RestaurantMenuIcon sx={{ fontSize: { xs: 48, md: 60 }, color: 'success.dark' }} />,
      path: '/food-list',
      color: '#2e7d32',
    },
    {
      title: t('home.features.iob.title'),
      description: t('home.features.iob.description'),
      icon: <CalculateIcon sx={{ fontSize: { xs: 48, md: 60 }, color: 'primary.main' }} />,
      path: '/calc/iob',
      color: '#1976d2',
    },
    {
      title: t('home.features.profiles.title'),
      description: t('home.features.profiles.description'),
      icon: <VaccinesIcon sx={{ fontSize: { xs: 48, md: 60 }, color: 'warning.main' }} />,
      path: '/profiles',
      color: '#ffa726',
    },
    {
      title: t('home.features.sensors.title'),
      description: t('home.features.sensors.description'),
      icon: <RadioButtonCheckedIcon sx={{ fontSize: { xs: 48, md: 60 }, color: 'success.main' }} />,
      path: '/sensors',
      color: '#2e7d32',
    },
    {
      title: t('home.features.ketones.title'),
      description: t('home.features.ketones.description'),
      icon: <ScienceIcon sx={{ fontSize: { xs: 48, md: 60 }, color: 'info.main' }} />,
      path: '/log/ketones',
      color: '#29b6f6',
    },
    {
      title: t('home.features.hba1c.title'),
      description: t('home.features.hba1c.description'),
      icon: <BloodtypeIcon sx={{ fontSize: { xs: 48, md: 60 }, color: 'error.main' }} />,
      path: '/log/hba1c',
      color: '#f44336',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          {t('home.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
          {t('home.subtitle')}
        </Typography>
      </Box>

      {initialized && !isAuthenticated && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 4,
            bgcolor: 'rgba(33, 150, 243, 0.08)',
            border: '1px solid rgba(33, 150, 243, 0.3)',
            color: 'text.primary',
            borderRadius: 2
          }}
        >
          <Typography variant="body1" gutterBottom>
            {t('home.loginRequired')}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<LoginIcon />}
              onClick={() => keycloak.login()}
            >
              {t('home.login')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => keycloak.register()}
            >
              {t('home.register')}
            </Button>
          </Box>
        </Alert>
      )}

      <Grid 
        container 
        spacing={{ xs: 2, md: 4 }} 
        justifyContent="center"
        alignItems="stretch"
      >
        {features.map((feature) => (
          <Grid item xs={12} sm={6} md={4} key={feature.title}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: { xs: 2, md: 3 } }}>
                <Box sx={{ mb: { xs: 1, md: 2 } }}>{feature.icon}</Box>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', md: '1rem' } }}>
                  {feature.description}
                </Typography>
              </CardContent>
              {isAuthenticated && (
                <CardActions sx={{ justifyContent: 'center', pb: { xs: 1.5, md: 2 } }}>
                  <Button
                    variant="contained"
                    size={isMobile ? 'small' : 'large'}
                    onClick={() => {
                      if (feature.external) {
                        window.open(feature.path, '_blank');
                      } else {
                        navigate(feature.path);
                      }
                    }}
                    sx={{
                      backgroundColor: feature.color,
                      '&:hover': {
                        backgroundColor: feature.color,
                        filter: 'brightness(0.9)',
                      },
                    }}
                  >
                    {feature.external ? t('home.openButton') : t('home.goToButton')}
                  </Button>
                </CardActions>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6, p: 3, backgroundColor: 'background.paper', borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          {t('home.aboutTitle')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('home.aboutDescription1')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('home.aboutDescription2')}
        </Typography>
        <Typography variant="body1">
          {t('home.aboutDescription3')}
        </Typography>
      </Box>
    </Container>
  );
}

export default Home;
